import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { Plus, Pin } from 'lucide-react';
import {
  useInfiniteConversations,
  useDeleteConversation,
  useUpdateConversation,
  useTogglePinConversation,
} from '../../hooks/useConversations';
import { useMeAgents as useAgents } from '../../hooks/useAgents';
import {
  Button,
  ConfirmModal,
  InlineEdit,
  Input,
  Table,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type { Conversation, Agent } from '../../types';
import { useConversationNamedEvent } from '../../hooks/useConversationNamedEvent';
import { routes } from '@/lib/routes';
import { cleanDisplayText } from '@/utils/display-text';

export default function ChatList() {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const {
    items,
    pinned,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteConversations();
  // Pinned conversations ship in their own always-complete array, excluded
  // from the paginated `items`. Surface both so they appear and are searchable.
  const conversations = [...pinned, ...items];
  const { data: agentsData } = useAgents();
  const deleteConversation = useDeleteConversation();
  const updateConversation = useUpdateConversation();
  const togglePinConversation = useTogglePinConversation();

  useConversationNamedEvent();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');

  // `Esc` focuses the chat search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus chat search',
      metadata: { group: 'Chats' },
    },
  );

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
      navigate(routes.operator.chatNew, { replace: true });
    }
  }, [searchParams, setSearchParams, navigate]);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    conversation: Conversation | null;
  }>({ isOpen: false, conversation: null });

  const agentMap = new Map(
    (agentsData?.items ?? []).map((a: Agent) => [a.agentId, a]),
  );

  const filtered = conversations.filter((c) => {
    const q = searchQuery.toLowerCase();
    const title = (cleanDisplayText(c.name) || 'New Conversation').toLowerCase();
    const agentName = (agentMap.get(c.agentId ?? '')?.name ?? '').toLowerCase();
    return title.includes(q) || agentName.includes(q);
  });

  const handleDelete = async () => {
    if (!deleteModal.conversation) return;
    try {
      await deleteConversation.mutateAsync(deleteModal.conversation.conversationId);
      success('Conversation deleted');
      setDeleteModal({ isOpen: false, conversation: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete conversation');
    }
  };

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (c: Conversation) => (
        <div>
          <p className="flex items-center gap-1.5 font-medium text-foreground">
            {c.pinned && (
              <Pin className="h-3.5 w-3.5 shrink-0 text-primary" aria-label="Pinned" />
            )}
            <InlineEdit
              value={c.name || ''}
              placeholder="New Conversation"
              onSave={async (name) => {
                await updateConversation.mutateAsync({
                  conversationId: c.conversationId,
                  input: { name: name || undefined },
                });
              }}
            />
          </p>
          <p className="text-muted-foreground text-xs mt-0.5">
            {c.messageCount} message{c.messageCount !== 1 ? 's' : ''}
          </p>
        </div>
      ),
    },
    {
      key: 'agent',
      header: 'Agent',
      render: (c: Conversation) => {
        const agent = agentMap.get(c.agentId ?? '');
        return (
          <span className="text-sm text-foreground">
            {agent?.name ?? c.agentId ?? '-'}
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Last Active',
      render: (c: Conversation) => (
        <span className="text-muted-foreground">
          {new Date(c.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (c: Conversation) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              togglePinConversation.mutate({
                conversationId: c.conversationId,
                pinned: !c.pinned,
              });
            }}
            className={
              c.pinned
                ? 'text-primary hover:text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }
            title={c.pinned ? 'Unpin' : 'Pin'}
            aria-label={c.pinned ? 'Unpin conversation' : 'Pin conversation'}
          >
            <Pin className={c.pinned ? 'h-4 w-4 fill-current' : 'h-4 w-4'} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, conversation: c });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Chat</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Conversations with your agents
          </p>
        </div>
        <Button onClick={() => navigate(routes.operator.chatNew)}>
          <Plus className="mr-2 h-4 w-4" />
          New Chat
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="flex-1 max-w-md">
          <Input
            ref={searchInputRef}
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <Table
        columns={columns}
        data={filtered}
        keyExtractor={(c) => c.conversationId}
        onRowClick={(c) => navigate(routes.operator.chat(c.conversationId))}
        emptyMessage="No chats yet. Start a new conversation to delegate work."
        isLoading={isLoading}
      />

      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Show more'}
          </Button>
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, conversation: null })}
        onConfirm={handleDelete}
        title="Delete Chat"
        message={`Are you sure you want to delete "${cleanDisplayText(deleteModal.conversation?.name) || 'New Conversation'}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteConversation.isPending}
      />
    </div>
  );
}
